import {
  DECISION_APPROVAL_LABELS,
  ACTION_STATUS_LABELS,
  AGENDA_ITEM_KIND_LABELS,
  DECISION_OUTCOME_LABELS,
  MEETING_LEVEL_LABELS,
  MEETING_STATUS_LABELS,
  MEETING_TYPE_LABELS,
  decisionHistorySchema,
  formatDate,
  formatDateTime,
  formatDecimal,
  formatMonth,
  minutesSchema,
  type DecisionOutcome,
  type DecisionOutcomeName,
  type OutcomePoint,
  type TrafficLight,
} from '@fluxus/shared';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ArrowRight, Download, FileText, History } from 'lucide-react';
import { Link } from 'react-router';
import { toast } from 'sonner';
import { EmptyState, ErrorState, LoadingState } from '@/components/states/States';
import { StatusBadge } from '@/components/status/StatusBadge';
import { Button } from '@/components/ui/button';
import { DecisionLine, Snapshot, meetingLink } from '@/features/meetings/CriticalAnalysis';
import { errorMessage } from '@/lib/api/errors';
import { useAuth, useCan } from '@/lib/auth/session';
import { saveBlobFile } from '@/lib/platform';
import { localParts } from '@/features/meetings/time';

/**
 * Ata da reunião encerrada (T-122b, PRD §11.25; DEC-051): o registro do que valia no encerramento — participantes,
 * pauta com os retratos, justificativas, decisões, ações, registro e a próxima reunião —, no escopo de quem vê, com
 * o PDF.
 */
export function MeetingMinutes({
  organizationId,
  projectId,
  meetingId,
  onOpenMeeting,
}: {
  organizationId: string;
  projectId: string;
  meetingId: string;
  onOpenMeeting: (id: string) => void;
}) {
  const { client } = useAuth();
  const url = `/organizations/${organizationId}/projects/${projectId}/meetings/${meetingId}/minutes`;
  const minutes = useQuery({
    queryKey: ['meetings', projectId, 'minutes', meetingId],
    queryFn: ({ signal }) => client.request(url, { schema: minutesSchema, signal }),
  });
  const pdf = useMutation({
    meta: { handlesError: true },
    mutationFn: async () => {
      const m = minutes.data!.meeting;
      await saveBlobFile({
        // A data da reunião no fuso das reuniões (não em UTC: depois das 21h seria o dia seguinte).
        fileName: `ata-${localParts(m.startsAt).day}.pdf`,
        title: `Ata — ${m.title}`,
        blob: await client.download(`${url}/pdf`),
      });
    },
    onError: (err) => toast.error('Não foi possível baixar o PDF da ata', { description: errorMessage(err) }),
  });

  if (minutes.isPending) return <LoadingState rows={4} label="Carregando a ata" />;
  if (minutes.isError) {
    return (
      <ErrorState
        error={minutes.error}
        onRetry={() => void minutes.refetch()}
        retrying={minutes.isFetching}
      />
    );
  }
  const a = minutes.data;
  const m = a.meeting;
  const decisions = a.items.flatMap((i) => i.decisions);
  const actions = decisions.filter((d) => d.action);
  return (
    <article aria-label={`Ata de ${m.title}`} className="flex flex-col gap-4 text-sm">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className="flex items-center gap-2 text-base font-bold">
            <FileText className="size-4" aria-hidden />
            Ata da reunião
          </h3>
          <p className="text-xs text-muted-foreground">
            Gerada no encerramento, em {formatDateTime(a.generatedAt)}
          </p>
        </div>
        <Button variant="outline" size="sm" loading={pdf.isPending} onClick={() => pdf.mutate()}>
          <Download aria-hidden />
          Baixar PDF
        </Button>
      </div>
      <dl className="grid gap-x-4 gap-y-1.5 sm:grid-cols-[max-content_1fr]">
        <dt className="font-semibold">Tipo e nível</dt>
        <dd>
          {MEETING_TYPE_LABELS[m.type]} · {MEETING_LEVEL_LABELS[m.level]}
        </dd>
        <dt className="font-semibold">Data</dt>
        <dd>
          {formatDateTime(m.startsAt)} · {m.durationMinutes} min
        </dd>
        <dt className="font-semibold">Encerrada</dt>
        <dd>
          {formatDateTime(m.closedAt)} por {m.closedByName}
        </dd>
        <dt className="font-semibold">Área / unidade</dt>
        <dd>{[m.department?.name, m.businessUnit?.name].filter(Boolean).join(' · ') || 'Empresa toda'}</dd>
        <dt className="font-semibold">Participantes</dt>
        <dd>{a.participants.length ? a.participants.map((p) => p.name).join(', ') : 'Ninguém convidado.'}</dd>
      </dl>
      {a.agenda ? (
        <section aria-label="Pauta">
          <h4 className="font-bold">Pauta</h4>
          <p className="mt-1 whitespace-pre-line break-words">{a.agenda}</p>
        </section>
      ) : null}
      {a.items.length ? (
        <section aria-label="Itens da pauta na ata" className="flex flex-col gap-3">
          <h4 className="font-bold">Itens da pauta</h4>
          <ol className="flex flex-col gap-3">
            {a.items.map((i, n) => (
              <li key={i.id} className="rounded-lg border border-border bg-surface p-3">
                <article aria-label={i.title} className="flex flex-col gap-2">
                  <p className="text-xs text-muted-foreground">
                    <span className="font-semibold tabular-nums">{n + 1}. </span>
                    <span className="font-semibold uppercase">{AGENDA_ITEM_KIND_LABELS[i.kind]}</span>
                    {i.department ? ` · ${i.department.name}` : ''}
                    {i.businessUnit ? ` · ${i.businessUnit.name}` : ''}
                  </p>
                  <h5 className="font-bold break-words">{i.title}</h5>
                  {i.detail ? <p className="break-words text-muted-foreground">{i.detail}</p> : null}
                  {i.snapshot || i.closing ? (
                    <div className="grid gap-2 sm:grid-cols-2">
                      {i.snapshot ? <Snapshot title="Na montagem da pauta" snap={i.snapshot} /> : null}
                      {i.closing ? <Snapshot title="No encerramento" snap={i.closing} /> : null}
                    </div>
                  ) : null}
                  <p>
                    <span className="font-semibold">Discutido: </span>
                    {i.discussed
                      ? `sim${i.discussedByName ? ` (marcado por ${i.discussedByName})` : ''}`
                      : 'não'}
                  </p>
                  {i.justification ? (
                    <p className="whitespace-pre-line break-words">
                      <span className="font-semibold">Justificativa: </span>
                      {i.justification}
                    </p>
                  ) : null}
                  {i.decisions.length ? (
                    <ul
                      aria-label={`Decisões de ${i.title}`}
                      className="flex flex-col gap-1.5 border-t border-border pt-2"
                    >
                      {i.decisions.map((d) => (
                        <li key={d.id}>
                          <p className="break-words">{d.text}</p>
                          <p className="flex flex-wrap gap-x-2 text-xs text-muted-foreground">
                            <span>Responsável: {d.responsibleName ?? 'não definido'}</span>
                            <span>Prazo: {d.dueDate ? formatDate(d.dueDate) : 'sem prazo'}</span>
                            {d.action || d.done || d.approvalStatus !== 'REJECTED' ? (
                              <span>
                                {d.action
                                  ? `Ação: ${ACTION_STATUS_LABELS[d.action.status]} no encerramento`
                                  : d.done
                                    ? 'Cumprida'
                                    : 'Pendente no encerramento'}
                              </span>
                            ) : null}
                            {d.approvalStatus !== 'NONE' ? (
                              <span>{DECISION_APPROVAL_LABELS[d.approvalStatus]} no encerramento</span>
                            ) : null}
                          </p>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </article>
              </li>
            ))}
          </ol>
        </section>
      ) : null}
      <section aria-label="Ações criadas">
        <h4 className="font-bold">Ações criadas</h4>
        {actions.length ? (
          <ul className="mt-1 flex flex-col gap-1">
            {actions.map((d) => (
              <li key={d.id} className="break-words">
                {d.action!.title} <span className="text-muted-foreground">({d.action!.planName})</span> ·{' '}
                {d.responsibleName ?? 'sem responsável'} · {d.dueDate ? formatDate(d.dueDate) : 'sem prazo'}
              </li>
            ))}
          </ul>
        ) : (
          <p className="mt-1 text-muted-foreground">Nenhuma ação criada nesta reunião.</p>
        )}
      </section>
      <section aria-label="Registro">
        <h4 className="font-bold">Registro</h4>
        <p className="mt-1 whitespace-pre-line break-words">{a.record ?? 'Sem registro.'}</p>
      </section>
      <section aria-label="Próxima reunião">
        <h4 className="font-bold">Próxima reunião</h4>
        {a.next ? (
          <p className="mt-1 flex flex-wrap items-center gap-2">
            <span>
              {a.next.title} · {formatDateTime(a.next.startsAt)}
            </span>
            <Button variant="outline" size="sm" onClick={() => onOpenMeeting(a.next!.id)}>
              <ArrowRight aria-hidden />
              Abrir a próxima reunião
            </Button>
          </p>
        ) : (
          <p className="mt-1 text-muted-foreground">Nenhuma próxima reunião agendada no encerramento.</p>
        )}
      </section>
      {a.hiddenItems || a.hiddenDecisions ? (
        <p className="text-xs text-muted-foreground">
          Fora do seu escopo de áreas e unidades, não aparecem nesta ata:{' '}
          {[
            a.hiddenItems ? (a.hiddenItems === 1 ? '1 item' : `${a.hiddenItems} itens`) : null,
            a.hiddenDecisions
              ? a.hiddenDecisions === 1
                ? '1 decisão'
                : `${a.hiddenDecisions} decisões`
              : null,
          ]
            .filter(Boolean)
            .join(' e ')}
          .
        </p>
      ) : null}
    </article>
  );
}

const OUTCOME_LIGHT: Record<DecisionOutcomeName, TrafficLight> = {
  IMPROVED: 'green',
  WORSENED: 'red',
  STABLE: 'yellow',
  WAITING: 'blue',
  NO_DATA_AFTER: 'gray',
  NO_DUE_DATE: 'gray',
  NO_BASELINE: 'gray',
  NOT_COMPARABLE: 'gray',
};

const pointText = (p: OutcomePoint) =>
  `${formatMonth(`${p.period}-15T12:00:00Z`)}: ${
    p.performance !== null
      ? `${formatDecimal(p.performance, 1)}% da meta`
      : `resultado ${formatDecimal(p.result, 2)}`
  }`;

/** "Melhorou" e o antes × depois que levaram a essa conclusão. */
function Outcome({ outcome, objective }: { outcome: DecisionOutcome; objective: boolean }) {
  return (
    <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
      <span className="font-semibold">{objective ? 'O objetivo melhorou?' : 'O indicador melhorou?'}</span>
      <StatusBadge status={OUTCOME_LIGHT[outcome.status]} label={DECISION_OUTCOME_LABELS[outcome.status]} />
      {outcome.before ? (
        <span className="text-muted-foreground">Antes ({pointText(outcome.before)})</span>
      ) : null}
      {outcome.after ? (
        <span className="text-muted-foreground">Depois ({pointText(outcome.after)})</span>
      ) : null}
    </p>
  );
}

/**
 * Histórico de decisões do KPI ou do objetivo (T-122b, PRD §19; DEC-051): por que estava crítico, o que foi
 * discutido e decidido, quem registrou, a ação, o prazo, se concluiu e se o indicador melhorou depois.
 */
export function DecisionHistoryList({
  organizationId,
  projectId,
  subject,
  subjectId,
  label,
}: {
  organizationId: string;
  projectId: string;
  subject: 'kpis' | 'objectives';
  subjectId: string;
  label: string;
}) {
  const { client } = useAuth();
  const can = useCan();
  const history = useQuery({
    queryKey: [subject, projectId, 'decision-history', subjectId],
    queryFn: ({ signal }) =>
      client.request(
        `/organizations/${organizationId}/projects/${projectId}/${subject}/${subjectId}/decision-history`,
        {
          schema: decisionHistorySchema,
          signal,
        },
      ),
  });
  if (history.isPending) return <LoadingState rows={3} label="Carregando o histórico de decisões" />;
  if (history.isError) {
    return (
      <ErrorState
        error={history.error}
        onRetry={() => void history.refetch()}
        retrying={history.isFetching}
      />
    );
  }
  if (!history.data.items.length) {
    return (
      <EmptyState
        className="py-8"
        icon={History}
        title="Nenhuma decisão registrada"
        description={`Quando uma análise crítica decidir algo sobre ${label}, a decisão aparece aqui com o que foi discutido e se o indicador melhorou depois.`}
      />
    );
  }
  return (
    <ol aria-label={`Histórico de decisões de ${label}`} className="flex flex-col gap-3 text-sm">
      {history.data.items.map(({ decision, meeting, item, outcome }) => (
        <li key={decision.id} className="rounded-lg border border-border bg-surface p-3">
          <article aria-label={decision.text} className="flex flex-col gap-2">
            <p className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              <span className="font-semibold">{meeting.title}</span>
              <span>{formatDate(meeting.startsAt)}</span>
              <span>{MEETING_LEVEL_LABELS[meeting.level]}</span>
              <span>{MEETING_STATUS_LABELS[meeting.status]}</span>
              {can('reunioes-decisoes', 'view') ? (
                <Link
                  className="font-medium text-brand-strong underline underline-offset-2 dark:text-brand"
                  to={meetingLink(organizationId, projectId, meeting.id)}
                >
                  Abrir a reunião
                </Link>
              ) : null}
            </p>
            <p>
              <span className="font-semibold">Por que estava crítico: </span>
              {AGENDA_ITEM_KIND_LABELS[item.kind]} — {item.title}
              {item.detail ? ` (${item.detail})` : ''}
            </p>
            {item.snapshot ? <Snapshot title="Na pauta" snap={item.snapshot} /> : null}
            <p className="whitespace-pre-line break-words">
              <span className="font-semibold">O que foi discutido: </span>
              {item.justification ??
                (item.discussed ? 'discutido, sem justificativa registrada' : 'não discutido')}
            </p>
            <div>
              <p className="font-semibold">O que foi decidido</p>
              <DecisionLine d={decision} organizationId={organizationId} projectId={projectId} />
              <p className="text-xs text-muted-foreground">Registrada por {decision.createdByName}</p>
            </div>
            <Outcome outcome={outcome} objective={!item.kpi} />
          </article>
        </li>
      ))}
    </ol>
  );
}
